import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

const EMPERORS = [
  {
    name: 'Aurelian',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019d5eda-d406-7518-8c9c-71c14bbbe5e1/9oitkgz6x1.webp',
  },
  {
    name: 'Vespasian',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019d53a4-0f28-7b73-8a04-52f007db10ae/vzm6ax157b.png',
  },
  {
    name: 'Constantine',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019f2a75-0a8d-7a36-b9bc-dc11618f3a17/lxoch7x1o1.webp',
  },
  {
    name: 'Hadrian',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019d53a5-435d-7a93-be07-dcb36b5027d2/ghov2bkx93.png',
  },
  {
    name: 'Augustus',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019f2cb6-6b23-730b-bba6-af2e95e06729/rbqf4co0xb.webp',
  },
  {
    name: 'Knossos',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019f04ed-6c6d-7481-862c-7c00797390b5/8k8xxdpfnz.png',
  },
  {
    name: 'Pius',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019ce90f-7d45-781b-b103-1e424cc3fe06/00l6ihqg48.webp',
  },
  {
    name: 'Brutus',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019c6d56-1fb1-7305-ba06-a8e38a3be286/jkdz2b47r1.png',
  },
  {
    name: 'Trajan',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019cd83e-b829-7c75-873a-594af0ccca95/cyc3wzx6zz.webp',
  },
  {
    name: 'Hannibal',
    url: 'https://69448200be4d4ffad9065e4d.featurebase-attachments.com/c/static/019ed3ea-9b33-746c-b4a6-792aeab4762f/y24cfm4k7t.png',
  },
];

const STAR_COUNT = 120;
const DIALOG_EXIT_MS = 400;
const FLASH_MS = 200;
const HANNIBAL_MS = 800;
const STARFIELD_LEAD_MS = 300;
const FADE_OUT_MS = 500;

function makeStars(): { angle: number; speed: number; delay: number }[] {
  const stars: { angle: number; speed: number; delay: number }[] = [];
  for (let i = 0; i < STAR_COUNT; i++) {
    stars.push({
      angle: (i / STAR_COUNT) * 360,
      speed: 0.4 + (((i * 7 + 13) % 17) / 17) * 0.8,
      delay: ((i * 3 + 7) % 19) / 19,
    });
  }
  return stars;
}

const STARS = makeStars();

interface TutorialOutroProps {
  onComplete: () => void;
  dialogTitle: string;
  dialogBody: string | ReactNode;
  stepLabel: string;
}

type Phase = 'dialog-exit' | 'starfield' | 'emperors' | 'fadeout' | 'done';

export function TutorialOutro({
  onComplete,
  dialogTitle,
  dialogBody,
  stepLabel,
}: TutorialOutroProps) {
  const [phase, setPhase] = useState<Phase>('dialog-exit');
  const [emperorIndex, setEmperorIndex] = useState(-1);
  const [bgReady, setBgReady] = useState(false);

  // Preload images
  useEffect(() => {
    EMPERORS.forEach(e => {
      const img = new Image();
      img.src = e.url;
    });
  }, []);

  // Trigger bg fade-in on next frame so CSS transition fires
  useEffect(() => {
    const raf = requestAnimationFrame(() => setBgReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Phase sequencing
  useEffect(() => {
    if (phase === 'dialog-exit') {
      const t = setTimeout(() => setPhase('starfield'), DIALOG_EXIT_MS);
      return () => clearTimeout(t);
    }
    if (phase === 'starfield') {
      const t = setTimeout(() => {
        setPhase('emperors');
        setEmperorIndex(0);
      }, STARFIELD_LEAD_MS);
      return () => clearTimeout(t);
    }
    if (phase === 'fadeout') {
      const t = setTimeout(() => {
        setPhase('done');
        onComplete();
      }, FADE_OUT_MS);
      return () => clearTimeout(t);
    }
  }, [phase]);

  // Emperor cycling — phase-transition state machine driven by timeouts
  useEffect(() => {
    if (phase !== 'emperors' || emperorIndex < 0) return;
    if (emperorIndex >= EMPERORS.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- animation state machine: emperor sequence → fadeout transition
      setPhase('fadeout');
      return;
    }
    const isLast = emperorIndex === EMPERORS.length - 1;
    const duration = isLast ? HANNIBAL_MS : FLASH_MS;
    const t = setTimeout(() => setEmperorIndex(prev => prev + 1), duration);
    return () => clearTimeout(t);
  }, [phase, emperorIndex]);

  const emperor =
    emperorIndex >= 0 && emperorIndex < EMPERORS.length
      ? EMPERORS[emperorIndex]
      : null;
  const isLast = emperorIndex === EMPERORS.length - 1;

  if (phase === 'done') return null;

  return (
    <div
      data-tutorial-outro=""
      className="fixed inset-0 overflow-hidden"
      style={{ zIndex: 400 }}
    >
      {/* Dark background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, #1a0a2e 0%, #000 70%)',
          opacity: bgReady && phase !== 'fadeout' ? 1 : 0,
          transition: `opacity ${phase === 'fadeout' ? FADE_OUT_MS : 400}ms ease`,
        }}
      />

      {/* Dialog grow+fade */}
      {phase === 'dialog-exit' && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ zIndex: 10 }}
        >
          <div
            style={{
              animation: `tutorialDialogExit ${DIALOG_EXIT_MS}ms ease-in forwards`,
            }}
          >
            <div className="border-chariot-stroke/20 bg-chariot-layer1 text-chariot-text-primary w-80 rounded-lg border p-4 shadow-lg">
              <div className="mb-1 flex items-center justify-between">
                <h3 className="text-sm font-semibold">{dialogTitle}</h3>
                <span className="text-chariot-text-secondary text-xs">
                  {stepLabel}
                </span>
              </div>
              <div className="text-chariot-text-secondary mb-4 text-sm">
                {dialogBody}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-chariot-text-secondary px-3 py-1.5 text-sm">
                  Skip
                </div>
                <div className="flex gap-2">
                  <div className="border-chariot-stroke rounded-md border px-3 py-1.5 text-sm">
                    Back
                  </div>
                  <div className="bg-chariot-brand-primary rounded-md px-3 py-1.5 text-sm text-white">
                    Done
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Starfield streaks */}
      {phase !== 'dialog-exit' && (
        <div
          className="absolute inset-0"
          style={{
            perspective: '400px',
            opacity: phase === 'fadeout' ? 0 : 1,
            transition: `opacity ${FADE_OUT_MS}ms ease`,
          }}
        >
          {STARS.map((star, i) => (
            <div
              key={i}
              className="absolute top-1/2 left-1/2"
              style={{
                width: '2px',
                height: '2px',
                marginLeft: '-1px',
                marginTop: '-1px',
                transform: `rotate(${star.angle}deg)`,
                transformOrigin: '0 0',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '2px',
                  background:
                    'linear-gradient(to right, rgba(200, 220, 255, 0.9), transparent)',
                  borderRadius: '1px',
                  animation: `tutorialStarStreak ${star.speed}s linear ${star.delay * 0.4}s infinite`,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Emperor images */}
      {emperor && (
        <div
          key={emperor.name}
          className="absolute inset-0 flex items-center justify-center"
          style={{
            animation: `tutorialEmperorIn ${isLast ? 300 : 150}ms ease-out forwards`,
          }}
        >
          <img
            src={emperor.url}
            alt={emperor.name}
            className="max-h-[70vh] max-w-[70vw] object-contain drop-shadow-2xl"
            style={{
              filter: 'brightness(1.1) contrast(1.05)',
              mixBlendMode: 'screen',
              opacity: isLast ? 0.85 : 0.6,
            }}
          />
          <div className="absolute bottom-[12%] text-center">
            <span
              className="text-2xl font-bold tracking-widest uppercase"
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                textShadow:
                  '0 0 20px rgba(239, 68, 68, 0.6), 0 2px 10px rgba(0,0,0,0.8)',
                animation: `tutorialNameIn ${isLast ? 300 : 150}ms ease-out forwards`,
              }}
            >
              {emperor.name}
            </span>
          </div>
        </div>
      )}

      {/* Vignette */}
      {phase !== 'dialog-exit' && phase !== 'fadeout' && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)',
          }}
        />
      )}

      <style>{`
        @keyframes tutorialDialogExit {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes tutorialStarStreak {
          0% { transform: translateX(0); width: 2px; opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateX(max(50vw, 50vh)); width: clamp(80px, 20vw, 200px); opacity: 0; }
        }
        @keyframes tutorialEmperorIn {
          0% { opacity: 0; transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes tutorialNameIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
